import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MentionModel, MentionType, ParticipantModel } from "@types";
import { concatNotNull } from "@utils";
import { Subject } from "rxjs";
import {
  EditMessageModelAction,
  EditMessageModelActionType,
  EditMessageModelMentionAction,
} from "../message-input/EditMessageModel";
import { ValidationError } from "../message-input/ValidationError";
import {
  MessageComposeModel,
  MessageInputComponent,
} from "../message-input/message-input.component";

@Component({
  selector: "app-message-compose-v2",
  templateUrl: "./message-compose-v2.component.html",
  styleUrls: ["./message-compose-v2.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MessageComposeV2Component implements OnChanges {
  @Input()
  public initialValue: MessageComposeModel | null = null;

  @Input()
  public userId: string;

  @Input()
  public participants: ParticipantModel[] = [];

  @Input()
  public maxLength: number = 3000;

  @Output()
  public submitEvent = new EventEmitter<MessageComposeModel>();

  @Output()
  public changeEvent = new EventEmitter<MessageComposeModel>();

  @Output()
  public validationEvent = new EventEmitter<ValidationError | null>();

  @Output()
  public pasteEvent = new EventEmitter<ClipboardEvent>();

  @Output()
  public userInputEvent = new EventEmitter<void>();

  @ViewChild("messageInput")
  public messageInput: MessageInputComponent | null = null;

  private mentionsSearchResultsSubject = new Subject<
    ParticipantModel[] | null
  >();

  public mentionsSearchResults$ =
    this.mentionsSearchResultsSubject.asObservable();

  private mentionAction: EditMessageModelMentionAction | null = null;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["participants"] && this.mentionAction) {
      this.updateMentionSearchResults(this.mentionAction);
    }
  }

  public handleSubmit(message: MessageComposeModel) {
    this.submitEvent.emit(message);
  }

  public handleChange(message: MessageComposeModel) {
    this.changeEvent.emit(message);
  }

  public handleAction(action: EditMessageModelAction | null) {
    if (action?.type !== EditMessageModelActionType.Mention) {
      this.mentionsSearchResultsSubject.next(null);
      this.mentionAction = null;
      return;
    }

    this.mentionAction = action;
    this.updateMentionSearchResults(action);
  }

  private updateMentionSearchResults(
    action: EditMessageModelMentionAction
  ): void {
    // Remove '@' from the start of the matched text
    const query = action.matchedText.substring(1).toLocaleLowerCase();

    const searchResults = this.participants.filter((p) => {
      const firstName = p.firstName?.toLocaleLowerCase() ?? "";
      const lastName = p.lastName?.toLocaleLowerCase() ?? "";
      const fullName = concatNotNull([firstName, lastName]);
      return (
        firstName.startsWith(query) ||
        lastName.startsWith(query) ||
        fullName.startsWith(query)
      );
    });

    this.mentionsSearchResultsSubject.next(searchResults);
  }

  public handleMention(participant: ParticipantModel) {
    const mention: MentionModel = {
      id: participant.userId,
      index: this.mentionAction.index,
      name: "@" + concatNotNull([participant.firstName, participant.lastName]),
      type: MentionType.User,
    };
    this.messageInput?.insertMention(mention);
    this.userInputEvent.next();
  }

  public handleValidationError(validationError: ValidationError | null) {
    this.validationEvent.emit(validationError);
  }

  public submit() {
    this.messageInput?.submit();
  }

  public setValue(message?: MessageComposeModel | null) {
    this.messageInput?.reset(message);
  }

  public insertText(text: string) {
    this.messageInput?.insertText(text);
    this.userInputEvent.next();
  }

  public handlePaste(event: ClipboardEvent) {
    this.pasteEvent.emit(event);
  }

  public handleUserInput() {
    this.userInputEvent.next();
  }

  public restoreFocus() {
    this.messageInput.restoreFocus();
  }
}
